import { Building2, Shield, User, type LucideIcon } from "lucide-react";
import paradiseRelax from "@/assets/paradise-relax.jpg";
import fachada from "@/assets/fachada.jpg";
import growthIllustration from "@/assets/growth-illustration.jpg";

export type AuthProfileKey = "guest" | "owner" | "admin";

export interface AuthProfileTheme {
  key: AuthProfileKey;
  label: string;
  tagline: string;
  image: string;
  imageAlt: string;
  icon: LucideIcon;
  cardBorder: string;
  cardHoverBorder: string;
  cardBg: string;
  iconBadge: string;
  bulletColor: string;
  buttonClass: string;
  overlay: string;
  pageGradient: string;
  accentText: string;
}

export const guestProfileTheme: AuthProfileTheme = {
  key: "guest",
  label: "Hóspede",
  tagline: "Quero reservar estadias",
  image: paradiseRelax,
  imageAlt: "Hóspede aproveitando estadia em resort",
  icon: User,
  cardBorder: "border-sky-200",
  cardHoverBorder: "hover:border-sky-400",
  cardBg: "bg-sky-50/60",
  iconBadge: "bg-sky-500 text-white shadow-sky-500/30",
  bulletColor: "bg-sky-500",
  buttonClass: "bg-sky-600 hover:bg-sky-700 text-white shadow-sky-600/20",
  overlay: "bg-gradient-to-t from-sky-950/85 via-sky-900/35 to-sky-800/10",
  pageGradient: "from-sky-100/90 via-background to-blue-50/40",
  accentText: "text-sky-700",
};

export const ownerProfileTheme: AuthProfileTheme = {
  key: "owner",
  label: "Proprietário",
  tagline: "Quero anunciar meu imóvel",
  image: fachada,
  imageAlt: "Fachada de imóvel disponível para anúncio",
  icon: Building2,
  cardBorder: "border-amber-200",
  cardHoverBorder: "hover:border-amber-400",
  cardBg: "bg-amber-50/60",
  iconBadge: "bg-amber-600 text-white shadow-amber-600/30",
  bulletColor: "bg-amber-600",
  buttonClass: "bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20",
  overlay: "bg-gradient-to-t from-amber-950/85 via-amber-900/35 to-amber-800/10",
  pageGradient: "from-amber-100/90 via-background to-orange-50/40",
  accentText: "text-amber-800",
};

export const adminProfileTheme: AuthProfileTheme = {
  key: "admin",
  label: "Administrador",
  tagline: "Acesso à área administrativa",
  image: growthIllustration,
  imageAlt: "Painel administrativo LiveStay",
  icon: Shield,
  cardBorder: "border-violet-200",
  cardHoverBorder: "hover:border-violet-400",
  cardBg: "bg-violet-50/60",
  iconBadge: "bg-violet-600 text-white shadow-violet-600/30",
  bulletColor: "bg-violet-600",
  buttonClass: "bg-violet-600 hover:bg-violet-700 text-white shadow-violet-600/20",
  overlay: "bg-gradient-to-t from-violet-950/85 via-violet-900/35 to-violet-800/10",
  pageGradient: "from-violet-100/90 via-background to-purple-50/40",
  accentText: "text-violet-800",
};

export const resolveAuthProfileTheme = (
  profile: "client" | "owner" | "admin" | null | undefined,
): AuthProfileTheme | null => {
  if (profile === "client") return guestProfileTheme;
  if (profile === "owner") return ownerProfileTheme;
  if (profile === "admin") return adminProfileTheme;
  return null;
};
